/**
 * The Customers list.
 *
 * No email column and no phone column, by design (uiux 6.1 and security 6.1). Contact lives
 * in the profile, masked, behind an audited reveal. A convenience column here would undo
 * that in one line of JSX, so it is not here and should not be added.
 */
import { useMemo, useState } from 'react';
import type { CustomerSegment, Role } from '@elutide/shared';
import { AgeText, Card, Chip, Column, DataTable, MoneyText, Pill, Skeleton } from './temp-ui';
import type { PillTone } from './temp-ui';
import { useCustomers } from './api';
import type { CustomerFilters } from './api';
import type { CustomerRow } from './types';
import { hasFullContact } from './types';

const SEGMENT_TONE: Record<CustomerSegment, PillTone> = {
  whale: 'copper',
  regular: 'info',
  one_timer: 'neutral',
  lapsed: 'warn',
};

const SEGMENT_LABEL: Record<CustomerSegment, string> = {
  whale: 'whale',
  regular: 'regular',
  one_timer: 'one-timer',
  lapsed: 'lapsed',
};

export const SEGMENT_FOOTNOTE =
  'Whale is the top of trailing twelve month spend or $1,500 with two or more orders. ' +
  'Regular is three or more paid orders in 180 days. Lapsed is quiet for twice this ' +
  'customer own median reorder interval, minimum 75 days. Test and staff accounts are ' +
  'excluded from every total.';

export function CustomerList({
  onOpen,
  role = 'owner',
}: {
  onOpen: (customerKey: string) => void;
  role?: Role;
}) {
  const [query, setQuery] = useState('');
  const [segment, setSegment] = useState<CustomerSegment | null>(null);
  const filters: CustomerFilters = useMemo(
    () => ({ ...(query ? { q: query } : {}), ...(segment ? { segment: [segment] } : {}) }),
    [query, segment],
  );
  const list = useCustomers(filters, null);

  const rows = list.data?.items ?? [];
  // The owner projection carries `email`; the masked one does not have the key at all.
  // Falling back to the role keeps the header stable while the first page is loading.
  const showsMoney = rows.length > 0 ? hasFullContact(rows[0]!) : role === 'owner';

  const columns: Column<CustomerRow>[] = [
    {
      id: 'customer',
      header: 'Customer',
      priority: 1,
      render: (row) => (
        <span>
          <strong>{row.display_name}</strong>
          {row.is_test ? <span style={{ color: 'var(--text-faint)' }}> · test</span> : null}
        </span>
      ),
    },
    {
      id: 'segment',
      header: 'Segment',
      priority: 1,
      render: (row) => <Pill tone={SEGMENT_TONE[row.segment]}>{SEGMENT_LABEL[row.segment]}</Pill>,
    },
    { id: 'orders', header: 'Orders', priority: 1, align: 'right', render: (row) => row.orders_paid },
    ...(showsMoney
      ? ([
          {
            id: 'lifetime',
            header: 'Lifetime',
            priority: 2,
            align: 'right',
            render: (row: CustomerRow) => <MoneyText cents={row.lifetime_net_cents ?? null} />,
          },
          {
            id: 'rr',
            header: 'RR bal.',
            priority: 2,
            align: 'right',
            render: (row: CustomerRow) => <MoneyText cents={row.rr_balance_cents ?? null} tone="var(--copper)" />,
          },
        ] as Column<CustomerRow>[])
      : []),
    {
      id: 'last_order',
      header: 'Last order',
      priority: 1,
      render: (row) =>
        row.last_paid_at ? <AgeText at={row.last_paid_at} /> : <span style={{ color: 'var(--text-faint)' }}>none paid</span>,
    },
    {
      id: 'tags',
      header: 'Tags',
      priority: 3,
      render: (row) => (
        <span style={{ display: 'inline-flex', gap: 'var(--space-1)', flexWrap: 'wrap' }}>
          {row.tags.map((tag) => (
            <Pill key={tag}>{tag}</Pill>
          ))}
        </span>
      ),
    },
  ];

  return (
    <Card
      title="Customers"
      action={
        <input
          type="search"
          aria-label="Search customers"
          placeholder="name, email, phone, order #"
          value={query}
          onChange={(event) => setQuery(event.currentTarget.value)}
          style={{
            padding: 'var(--space-2)',
            minWidth: 240,
            borderRadius: 'var(--radius-input)',
            border: 'var(--border-width) solid var(--border)',
            background: 'var(--surface-2)',
            color: 'var(--text)',
          }}
        />
      }
    >
      <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-3)', flexWrap: 'wrap' }}>
        <Chip label="All" active={segment === null} onClick={() => setSegment(null)} />
        {(Object.keys(SEGMENT_LABEL) as CustomerSegment[]).map((value) => (
          <Chip
            key={value}
            label={SEGMENT_LABEL[value]}
            active={segment === value}
            onClick={() => setSegment(segment === value ? null : value)}
          />
        ))}
      </div>

      {list.isLoading ? (
        <Skeleton shape="text" lines={6} />
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(row) => row.customer_key}
          onRowClick={(row) => onOpen(row.customer_key)}
          emptyState="No customers match those filters."
          caption={SEGMENT_FOOTNOTE}
        />
      )}
    </Card>
  );
}
